import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useReducer } from "react";

import {UpdatingAd,clearMessage} from "@/services/adServices/adActions";
import { getClients } from "@/services/clientService/clientActions";
import { getAllProducts } from "@/services/productServices/productActions";
import { useImageUpload } from "@/services/ImageServices/useImageUpload";
import NotificationAlertOptions from "@/layouts/Alerts";
import UpdateAdView from "./UpdateAdView";
import { asCaught } from "@/services/caughtError";
import { adForm, clientOptions, productOptions, updateFormState, type AdRow } from "./adForm";

const POSITIONS = [
  { value: "right", label: "Right" },
  { value: "left", label: "Left" },
  { value: "top", label: "Top" },
  { value: "bottom", label: "Bottom " },
  { value: "inListing", label: "In Listing " },
];

// The row type moved to the form slice (SCRUM-2219); re-exported for ManageAds.
export type { AdRow } from "./adForm";

const formActions = adForm.actions;

type UpdateAdContainerProps = {
  ad?: AdRow;
};

export default function UpdateAdContainer({ ad: adProp }: UpdateAdContainerProps) {
  const dispatch = useAppDispatch();
  const uploadImage = useImageUpload();

  const productsFromStore = useAppSelector((s) => s.products.products);
  const clientsFromStore  = useAppSelector((s) => s.clients.clients);
  const message           = useAppSelector((s) => s.ad.message) as any;
  const errors            = useAppSelector((s) => s.errors) as any;
  const site              = useAppSelector((s) => s.sites.site);

  // SCRUM-2219: the form state is one createSlice (adForm.ts), mounted locally;
  // the handlers below only dispatch its named actions.
  const [form, dispatchForm] = useReducer(adForm.reducer, adProp, updateFormState);
  const { ad, client, product, countryCode, loading } = form;

  // ManageAds mounts this screen without a key, so a second row's Edit re-feeds it.
  useEffect(() => {
    if (adProp) dispatchForm(formActions.adLoaded(adProp));
  }, [adProp]);

  useEffect(() => {
    dispatch(getClients());
    dispatch(getAllProducts());
  }, [dispatch]);

  useEffect(() => {
    if (errors?.errors) {
      dispatchForm(formActions.saveFailed());
    }
  }, [errors, dispatch]);

  useEffect(() => {
    if (message) {
      NotificationAlertOptions("success", message);
      dispatch(clearMessage());
      dispatchForm(formActions.saved());
    }
  }, [message, dispatch]);

  const onChange = (e) => {
    const { name, value } = e.target;
    dispatchForm(formActions.fieldChanged({ name, value }));
  };

  const onSelectPosition = (opt) => dispatchForm(formActions.positionSelected(opt));
  const onSelectClient = (opt) => dispatchForm(formActions.clientSelected(opt));
  const onSelectProduct = (opt) => dispatchForm(formActions.productSelected(opt));
  const onImageUpload = (img) => dispatchForm(formActions.imagePicked(img));

  const onClickUpdate = async () => {
    // Freshly picked file: upload first, then save inline with the returned URL
    // (useImageUpload clears images.url on success). The ad object already carries
    // _id + client/product, so { ...ad, adsMedia: url } preserves them. The
    // secure_url and no-image direct-save branches are unchanged.
    const media = ad.adsMedia;
    if (media && typeof media === "object" && media.data) {
      const cc = countryCode || site?.country_code || "";
      const path = `/${cc}/BANNERS/${media.imgName}`;
      dispatchForm(formActions.uploadStarted());
      try {
        const url = await uploadImage(media.data, path);
        dispatch(UpdatingAd({ ...ad, adsMedia: url }));
      } catch (err) {
        dispatchForm(formActions.saveFailed());
        NotificationAlertOptions(
          "error",
          asCaught(err)?.response?.data?.message ||
            asCaught(err)?.message ||
            "Saving the ad failed. Please try again."
        );
      }
      return;
    }

    if (media && typeof media === "object" && media.secure_url) {
      const payload = { ...ad, adsMedia: media.secure_url };
      dispatch(UpdatingAd(payload));
      return;
    }
    dispatch(UpdatingAd(ad));
  };

  // Options are derived during render from the store lists.
  const products = productOptions(productsFromStore);
  const clients = clientOptions(clientsFromStore);
  const positionValue = POSITIONS.find((p) => p.value === ad.adsPosition) || null;
  const clientValue   = client
    ? clients.find((c) => c.value === client.value) || null
    : null;
  const productValue  = product
    ? products.find((p) => p.value === product.value) || null
    : null;

  return (
    <UpdateAdView
      loading={loading}
      ad={ad}
      positions={POSITIONS}
      positionValue={positionValue}
      clients={clients}
      clientValue={clientValue}
      products={products}
      productValue={productValue}
      onChange={onChange}
      onSelectPosition={onSelectPosition}
      onSelectClient={onSelectClient}
      onSelectProduct={onSelectProduct}
      onClickUpdate={onClickUpdate}
      onImageUpload={onImageUpload}
    />
  );
}
